import React, { useEffect, useState } from 'react';
import { SelectPicker, Table, DOMHelper, Stack, IconButton, Button, Input, Whisper, Tooltip, Divider, SortType } from 'rsuite';
import { BaseErrorModel, PagedResponseModel, PaginationFilterResult } from '@/models/core.model';
import { Achievement, AchievementPaginationFilterRequest, AchievementTypeList } from '@/models/achievement.model';
import { getPagedMainAchievements } from '@/services/achievement.service';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@rsuite/icons/Edit';
import { AxiosError } from 'axios';
import { toastErrorMessage } from '@/utils/toasterMessage';
import FetchingSelect from '@/components/FetchingSelect';
import TablePagination from '@/components/TablePagination';
import { getCitiesLookup } from '@/services/city.service';
import { getCountriesLookup } from '@/services/country.service';
import ImageCell from '@/components/ImageCell';
import PageContent from '@/components/PageContent';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

const AchievementList = () => {
  const [filterReq, setFilterReq] = useState<AchievementPaginationFilterRequest>(new AchievementPaginationFilterRequest());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());  
  const [items, setItems] = useState<Achievement[]>([]);
  const [loading, setLoading] = useState(true);

  const nav = useNavigate();

  useEffect(() => {
    fetchData(filterReq);
  }, [filterReq]);

  const fetchData = async (req: AchievementPaginationFilterRequest) => {
    setLoading(true);
    try {
      // const finalReq = {
      //   ...req,
      // };
      const result: PagedResponseModel<Achievement> = await getPagedMainAchievements(req);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({ 
          ...pre, 
          pageNumber: result.pageNumber, 
          totalRecords: result.totalRecords, 
          pageSize: result.pageSize 
        }));
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching achievements:${errorModel?.errorMessage}`);
    }
    finally {
      setLoading(false);
    }
  };
  
  const handleSortColumn = (sortColumn: any, sortType?: SortType) => {
    setFilterReq(pre => ({
      ...pre, 
      orderBy: sortColumn,
      isAscending: sortType == 'asc'
    }));
  };

  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Button appearance="primary" onClick={() => nav('/achievements/new')}>
          Add New Achievement
        </Button>
        <Stack spacing={6}>
          <FetchingSelect
            label="City"
            fetchData={(text, page) => getCitiesLookup(text, undefined, page)}
            value={filterReq.cityId}
            onChange={dataKey => setFilterReq(pre => ({...pre, cityId: dataKey}))}
          />
          <FetchingSelect
            label="Country"
            fetchData={(text, page) => getCountriesLookup(text, page)}
            value={filterReq.countryId}
            onChange={dataKey => setFilterReq(pre => ({...pre, countryId: dataKey}))}
          />
          <SelectPicker
            label="Type"
            data={AchievementTypeList}
            searchable={false}
            value={filterReq.type ?? null}
            onChange={dataKey => setFilterReq(pre =>({ ...pre, type: dataKey ?? undefined}))}
          />            
          <Input 
            placeholder="Keyword" 
            value={filterReq.keyword ?? ''} 
            onChange={dataKey => setFilterReq(pre => ({...pre, keyword: dataKey}))}
          />
        </Stack>
      </Stack>

      <Table
        height={Math.max(getHeight(window) - 350, 400)}
        data={items}
        sortColumn={filterReq.orderBy}
        sortType={filterReq.isAscending ? 'asc' : 'desc'}
        onSortColumn={handleSortColumn}
        loading={loading}
      >
        <Column flexGrow={0.5} align="center" >
          <HeaderCell>Icon</HeaderCell>
          <ImageCell dataKey="profilePicture" />
        </Column>            
        <Column flexGrow={3} align="left" fullText sortable>
          <HeaderCell>Name</HeaderCell>
          <Cell dataKey="name" />
        </Column>
        <Column flexGrow={2} align="left" fullText sortable>
          <HeaderCell>Type</HeaderCell>
          <Cell dataKey='type'>
            {(rowData : Achievement) => AchievementTypeList.findLast(t => t.value == rowData.type)?.label ?? ''}
          </Cell>
        </Column>
        <Column flexGrow={2} align="center" fullText sortable>
          <HeaderCell>Unique Number</HeaderCell>
          <Cell dataKey="uniqueNumber" />
        </Column>
        <Column flexGrow={1} fixed="right" fullText>
          <HeaderCell>...</HeaderCell>
          <Cell style={{ padding: '6px' }}>
            {(rowData: Achievement) => 
              <Stack spacing={5}>
                <Whisper placement="top" trigger="hover" speaker={<Tooltip>Edit Achievement</Tooltip>}>
                  <IconButton onClick={() => nav('/achievements/' + rowData.id)} icon={<EditIcon />} circle size="sm" />
                </Whisper>
              </Stack>
            }
          </Cell>
        </Column>
      </Table>
      <Divider />
      <TablePagination 
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={dataKey => setFilterReq(pre => ({...pre, pageNumber: dataKey}))}
        onChangeLimit={dataKey => setFilterReq(pre => ({...pre, pageSize: dataKey}))}
      />
    </PageContent>
  );
};

export default AchievementList;
